import { z } from 'zod'
import type { DecryptionState, ElectionKeyMode, ElectionStatus } from '../enums'

/*
 * After an Election-committee voting ends, its results wait for PEOPLE (the committee's organizer opens them, then
 * t members unlock them in the DAVINCI Council app) before anything is counted. Status badges must say so instead
 * of "Counting" (which is what the voting does once the committee has acted, and what Automatic votings do).
 */

export const RESULTS_WAIT_STAGES = ['committee_opening', 'committee_keys'] as const
export const ResultsWaitStageSchema = z.enum(RESULTS_WAIT_STAGES)
export type ResultsWaitStage = z.infer<typeof ResultsWaitStageSchema>

export const ResultsWaitSchema = z.object({
  /** committee_opening: the committee has not opened the results; committee_keys: open, fewer than t unlocked. */
  stage: ResultsWaitStageSchema,
  /** Members who unlocked so far (least advanced question; 0 while waiting for the opening). */
  done: z.number().int().min(0),
  /** t of the committee (null while unknown). */
  threshold: z.number().int().min(1).nullable(),
  /** n of the committee (null while unknown). */
  size: z.number().int().min(1).nullable(),
})
export type ResultsWait = z.infer<typeof ResultsWaitSchema>

interface DecryptionLike {
  state: DecryptionState
  partialsDone?: number | null
  threshold?: number | null
  size?: number | null
}

/**
 * Whether the voting's results wait for its election committee, from the per-question decryption states the sync
 * worker stores. Null when they do not: Automatic votings, still in the grace window, a committee that already
 * unlocked (being combined / published), results stored, or nothing observed yet.
 */
export function resultsWaitOf(input: {
  keyMode: ElectionKeyMode
  status: ElectionStatus
  decryptions: readonly (DecryptionLike | null | undefined)[]
  threshold?: number | null
  size?: number | null
}): ResultsWait | null {
  if (input.keyMode !== 'council') return null
  if (input.status !== 'closing' && input.status !== 'ended') return null
  const pending = input.decryptions.filter((d): d is DecryptionLike => Boolean(d) && d!.state !== 'results')
  if (!pending.length) return null
  const threshold = input.threshold ?? pending.find((d) => d.threshold)?.threshold ?? null
  const size = input.size ?? pending.find((d) => d.size)?.size ?? null
  if (pending.some((d) => d.state === 'awaiting-opening'))
    return { stage: 'committee_opening', done: 0, threshold, size }
  const unlocking = pending.filter((d) => d.state === 'decrypting')
  if (!unlocking.length) return null
  const done = Math.min(...unlocking.map((d) => d.partialsDone ?? 0))
  if (threshold !== null && done >= threshold) return null // every needed key turned: being combined
  return { stage: 'committee_keys', done, threshold, size }
}
